import { Directive, ElementRef, NgZone, OnDestroy, OnInit, inject } from '@angular/core';

import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';

import { CloseOnScroll } from './close-on-scroll';

/**
 * Dismisses an ngb dropdown as soon as the user scrolls — the dropdown counterpart of
 * {@link ClosePopoverOnScrollDirective}.
 *
 * The row actions menu (Chat / Cancel …) uses `container="body"` so the table's scroll
 * viewport can't clip it, which also means it doesn't follow its ⋮ trigger when the
 * rows or the page scroll. Closing on the first scroll keeps it from drifting over
 * other rows.
 *
 * Auto-applies to every `[ngbDropdown]` in the modules that import it. Listeners are
 * attached only while the menu is open; see {@link CloseOnScroll} for the scroll
 * sources covered.
 */
@Directive({
  selector: '[ngbDropdown]',
  standalone: true,
})
export class CloseDropdownOnScrollDirective implements OnInit, OnDestroy {
  private readonly dropdown = inject(NgbDropdown);

  private readonly subscriptions = new Subscription();
  private readonly closer = new CloseOnScroll(
    inject<ElementRef<HTMLElement>>(ElementRef).nativeElement,
    inject(NgZone),
    () => this.dropdown.isOpen(),
    () => {
      this.dropdown.close();
    },
  );

  ngOnInit(): void {
    this.subscriptions.add(this.dropdown.openChange.subscribe((isOpen: boolean) => {
      if (isOpen) {
        this.closer.attach();
      } else {
        this.closer.detach();
      }
    }));
  }

  ngOnDestroy(): void {
    this.closer.detach();
    this.subscriptions.unsubscribe();
  }
}
